import { averageDelta } from './array.js';
import { isModifierKey, ARROW_KEYS } from './key-code.js';
import { isStandardShortcutKeyPressed } from './modifiers.js';
import { HAS_WINDOW } from './window.js';

const PRESSED_KEYS = new Set();
const REPEAT_TIMES = [];
let REPEAT_RATE = 0;
const pressedKeys = {
    /**
     * Set of currently pressed keys. Useful when you need to get the state of multiple keys
     * imperatively, rather than right when the keydown/keyup event occurs.
     *
     * Typically you should call `PressedKeys.isPressed(code)` instead of checking the set directly,
     * so that you don't need to worry about left/right modifier keys.
     *
     * @note This stores the `code` property of the KeyboardEvent, which differentiates between the
     * left and right versions of keys like Shift, Control, Alt, and Meta. It does not differentiate
     * between the state of the `shift` key, so always `KeyA` and not `a` or `A`. This makes it so we
     * can consider a meta key held down even if you switch between the left and right version.
     *
     * @note On MacOS, keyup events are not fired if the command key is pressed. While the command key
     * is held, PRESSED_KEYS will only contain the Meta key.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
     */
    raw: PRESSED_KEYS,
    /**
     * Check if a specific key is pressed.
     *
     * Allows checking keys that normally differentiate between left and right. For example you can
     * check "Shift" to see if either ShiftLeft or ShiftRight is pressed. You can still check specific
     * sides by using the more specific "ShiftLeft" or "ShiftRight" codes.
     */
    isPressed: isKeyPressed,
    /** Convenience function to get the current state of the arrow keys. */
    arrows() {
        return ARROW_KEYS.filter((key) => PRESSED_KEYS.has(key));
    },
    /**
     * Convenience function to get the current state of the modifier keys. Returns an object containing
     * the same modifier key names that events do, to make it easy to reuse other helper functions.
     *
     * Typically you would just read these off of the event, but sometimes you need
     * access to modifiers outside of an event handler.
     */
    modifiers() {
        return {
            altKey: isKeyPressed('Alt'),
            metaKey: isKeyPressed('Meta'),
            shiftKey: isKeyPressed('Shift'),
            ctrlKey: isKeyPressed('Control'),
        };
    },
    standardShortcut() {
        return isStandardShortcutKeyPressed(this.modifiers());
    },
    /** Convenience function to check if any arrow keys are pressed. */
    anyArrows() {
        return ARROW_KEYS.some((key) => PRESSED_KEYS.has(key));
    },
    /**
     * Get the key repeat rate, in milliseconds, based on the user's current keyboard repeat settings.
     * This isn't something we can query directly, instead we store the time between repeated keydown
     * events and average them. This can be useful for emulating key repeat behavior in a setTimeout
     * and utilizing PressedKeys to determine which keys are held.
     *
     * Returns 0 if there haven't been enough repeated keydown events to calculate a repeat rate yet.
     *
     * @note The `repeatRate` is computed ONCE after enough information is available. If the user
     * changes their keyboard repeat settings after that, they would have to refresh their browser
     * for the new rate to be picked up. This is an okay tradeoff because it lets us start subsequent
     * repeating behaviors much sooner, and key repeat rates are typically updated once when someone
     * sets up a new machine and then never touched again.
     */
    repeatRate() {
        return REPEAT_RATE;
    },
};
/**
 * An interface for checking what keyboard keys are currently pressed (held down).
 *
 * Handles edge cases for you such as:
 *
 * - The browser is not focused when the keyup event occurs (pressed key state is cleared on blur).
 * - On MacOS, keyup events are not fired for non-modifier keys while the Meta key is held.
 *   In this case, only modifier keys are tracked while Meta is held - other keys are cleared
 *   from the pressed keys state.
 *
 * @note This stores the `code` property of the KeyboardEvent, which differentiates between the
 * left and right versions of keys like Shift, Control, Alt, and Meta. It does not differentiate
 * between the state of the `shift` key, so always `KeyA` and not `a` or `A`. This makes it so we
 * can consider a meta key held down even if you switch between the left and right version.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
 */
const PressedKeys = pressedKeys;
const KEY_CODE_MAP = {
    Shift: ['ShiftLeft', 'ShiftRight'],
    Control: ['ControlLeft', 'ControlRight'],
    Meta: ['MetaLeft', 'MetaRight'],
    Alt: ['AltLeft', 'AltRight'],
    Enter: ['Enter', 'NumpadEnter'],
    EnterLeft: ['Enter'],
    Key0: ['Digit0', 'Numpad0'],
    Key1: ['Digit1', 'Numpad1'],
    Key2: ['Digit2', 'Numpad2'],
    Key3: ['Digit3', 'Numpad3'],
    Key4: ['Digit4', 'Numpad4'],
    Key5: ['Digit5', 'Numpad5'],
    Key6: ['Digit6', 'Numpad6'],
    Key7: ['Digit7', 'Numpad7'],
    Key8: ['Digit8', 'Numpad8'],
    Key9: ['Digit9', 'Numpad9'],
};
////////////////////////////////////////////////////////////////////////////////////////////////////
if (HAS_WINDOW) {
    window.addEventListener('keydown', updatePressedKeys, true);
    window.addEventListener('keyup', updatePressedKeys, true);
    window.addEventListener('blur', clearPressedKeys);
}
////////////////////////////////////////////////////////////////////////////////////////////////////
function updatePressedKeys(e) {
    const isDown = e.type === 'keydown';
    // On MacOS, non-modifier keyup events are not fired while the Meta key is held.
    // So, for safety, we only track modifier keys when Meta is held.
    if (isDown) {
        // If the Meta key is already pressed, ignore non-modifier keys.
        const isExistingMeta = isKeyPressed('Meta');
        if (isExistingMeta && !isModifierKey(e.code))
            return;
        // If the Meta key is newly pressed, clear all non-modifier keys.
        const isNewMeta = e.key === 'Meta';
        if (isNewMeta) {
            for (const key of PRESSED_KEYS) {
                if (!isModifierKey(key))
                    PRESSED_KEYS.delete(key);
            }
        }
        PRESSED_KEYS.add(e.code);
    }
    else {
        PRESSED_KEYS.delete(e.code);
    }
    if (!REPEAT_RATE)
        recordRepeat(e);
}
function clearPressedKeys() {
    PRESSED_KEYS.clear();
    REPEAT_TIMES.length = 0;
}
function isKeyPressed(code) {
    const mapping = KEY_CODE_MAP[code];
    if (mapping)
        return mapping.some((key) => PRESSED_KEYS.has(key));
    return PRESSED_KEYS.has(code);
}
function recordRepeat(e) {
    if (!e.repeat) {
        REPEAT_TIMES.length = 0;
        return;
    }
    REPEAT_TIMES.push(performance.now());
    if (REPEAT_TIMES.length >= 4) {
        REPEAT_RATE = averageDelta(REPEAT_TIMES);
        REPEAT_TIMES.length = 0;
    }
}

export { PressedKeys };
//# sourceMappingURL=pressed-keys.js.map
